import { Modal, useModals } from 'almanac-ui/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import BaseStyles from 'components/BlazeCommandLine/BlazeCommandLine.module.scss'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { InlineLocationPicker } from 'components/InlineLocationPicker/InlineLocationPicker'
import { LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { useBrand } from 'hooks/useBrand'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { startCase } from 'lodash'
import { useState } from 'react'
import { DOC_PATH } from 'paths'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { layersKeys } from 'hooks/queries/layers/layersKeys'
import { generatePath, useHistory } from 'react-router-dom'
import { AlmanacFile, Selection as JsonSelection } from 'types'
import ModalStyles from '../BlazeCommandLine.module.scss'
import { useQueryClient } from 'react-query'
import { useDocGeneration } from '../hooks/useDocGeneration'
import { Selection } from 'prosemirror-state'
import { TagInput } from 'components/TagInput/TagInput'
import { EditableModalTitle } from 'components/EditableModalTitle/EditableModalTitle'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { VoicesAndTonesSelect } from 'components/DocRightRail/screens/AIAssistant/VoicesAndTonesSelect'
import { reportError } from 'utils/error'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { GenerationType } from '../api/createDocGenerationRequest'

export const TurnIntoCustomizeModal = ({
  layerId,
  articleId,
  content,
  isInline,
  channelType,
  isTurnInto,
  selection,
  usePrompt = true,
}: {
  layerId?: string
  articleId?: string
  content: string
  selection?: Selection
  isInline: boolean
  isTurnInto?: boolean
  channelType: ChannelType | GenerationType
  usePrompt?: boolean
}) => {
  const { closeModal, stack, closeAllModals } = useModals()
  const { isMobile } = useResponsiveDesign()
  const showBackButton = stack.length > 1
  const workspaceId = useCurrentWorkspaceId()
  const history = useHistory()
  const [title, setTitle] = useState('')
  const [tones, setSelectedTones] = useState<Tone[]>([])
  const [voice, setSelectedVoice] = useState<number>()
  const queryClient = useQueryClient()
  const { currentFolder } = useCurrentFolderStore()
  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder | AlmanacFile | null>(currentFolder || null)
  const [prompt, setPrompt] = useState(
    (() => {
      if (channelType.endsWith('s')) return `Write ${startCase(channelType)} about ${content ?? ''}`
      return `Write a ${startCase(channelType)} about ${content ?? ''}`
    })(),
  )

  const [seoWords, setSeoWords] = useState<string[]>([])

  const { defaultFoldersLocation } = useBrand()

  const { handleGenerate, isCreating } = useDocGeneration({
    onSuccess: async ({ almanac_file }) => {
      if (almanac_file.slug) {
        const docPath = generatePath(DOC_PATH, { slug: almanac_file.slug })
        history.push(docPath)
      } else {
        reportError('Could not redirect to doc')
      }

      let path = generatePath(DOC_PATH, { slug: almanac_file.id })

      if (articleId && layerId) {
        await Promise.all([
          queryClient.invalidateQueries([layersKeys.prefix, 'detail', layerId]),
          queryClient.invalidateQueries([layersKeys.prefix, articleId, 'list']),
        ])
      }

      if (!isInline) {
        history.push(path)
      }

      closeAllModals()
    },
  })

  const handleSelectVoice = (voice?: number) => {
    if (tones.length) setSelectedTones([])
    setSelectedVoice(voice)
  }

  const handleSelectTones = (tones: Array<Tone>) => {
    if (voice) setSelectedVoice(undefined)
    setSelectedTones(tones)
  }

  const onGenerate = async () => {
    const requestArticle: { articleId: string } | { projectId: number | string | undefined } =
      isInline && articleId
        ? { articleId }
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          { projectId: selectedFolder?.id }
    handleGenerate({
      workspaceId,
      prompt: {
        topic: usePrompt ? prompt : content,
        generation_type: channelType,
        brand_voice_id: voice,
        tone: tones,
        seo_words: seoWords,
      },
      ...requestArticle,
      title,
      isTurnInto,
      selection: isInline ? (selection?.toJSON() as JsonSelection) : undefined,
    })
  }

  const locationPicker = (
    <div className={BaseStyles.locationPicker}>
      <span className={BaseStyles.topLabel}>Create in</span>
      <InlineLocationPicker
        buttonLabel={selectedFolder?.title || defaultFoldersLocation}
        dropdownTriggerProps={{ placement: 'bottom-start', offsetOptions: { mainAxis: -40, crossAxis: 0 } }}
        locationPickerProps={{
          actionType: 'create',
          initialFolder: (currentFolder as LocationPickerFolder) || null,
          initialWorkspaceId: workspaceId,
          onSelectFolder: ({ selectedFolder }) => setSelectedFolder(selectedFolder),
          isRootDisabled: false,
        }}
      />
    </div>
  )

  return (
    <Modal.Root>
      {!isInline && <Modal.Top>{locationPicker}</Modal.Top>}

      {isInline ? (
        <Modal.Header title={`Customize ${startCase(channelType)}`} />
      ) : (
        <Modal.Header>
          {!isInline && (
            <EditableModalTitle value={title} onChange={setTitle} placeholder={`Untitled ${startCase(channelType)}`} />
          )}
        </Modal.Header>
      )}

      <Modal.Content>
        {!isInline && isMobile && locationPicker}
        {usePrompt && (
          // Only show the textarea if we don't have the article markdown
          // which would happen if we were passed a selection
          <>
            <div className={ModalStyles.subtitle}>
              Try to be as detailed as possible with your prompt help blaze generate something unique
            </div>
            <div className={BaseStyles.section}>
              <Input
                size="xl"
                data-testid="prompt-input"
                autoFocus
                isTextarea
                onChange={(e) => setPrompt(e.currentTarget.value)}
                value={prompt}
                name="prompt"
                maxHeight={144}
                className={BaseStyles.promptInput}
              />
            </div>
          </>
        )}

        <div className={BaseStyles.section}>
          <span className={BaseStyles.label}>Select your brand voice</span>
          <VoicesAndTonesSelect
            className={BaseStyles.voicesAndTones}
            onVoicesChange={handleSelectVoice}
            onTonesChange={handleSelectTones}
            selectedVoice={voice}
            selectedTones={tones}
            isVoicesInline
            isTonesInline
          />
        </div>

        <div className={BaseStyles.section}>
          <span className={BaseStyles.label}>SEO keywords to include</span>
          <TagInput tags={seoWords} onChange={(newTags) => setSeoWords(newTags)} />
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {showBackButton ? (
            <Modal.FooterButton onPress={() => closeModal()} size="lg" variant="tertiary">
              Back
            </Modal.FooterButton>
          ) : null}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={onGenerate} size="lg" variant="primary" isDisabled={isCreating}>
            Generate
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
